import {
  API_BASE_URL,
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  LOGIN_PENDING_REDIRECT_COOKIE,
} from "../constants/constants";
import { authEndpoints } from "../services/endpoints";
import SignolUtils from "./signolUtils";
import StorageUtils from "./storageUtils";
import { trackError } from "./userActivityTracker";
import {
  queryClient,
  queryClient as QueryClientSingleton,
} from "../utils/queryClient";

// private method to refresh token and retry original request
const refreshTokenAndRetry = async (refreshToken, originalRequestOptions) => {
  try {
    const { accessToken: newAccessToken } =
      await authEndpoints.refreshToken(refreshToken);
    StorageUtils.setItem(ACCESS_TOKEN_COOKIE, newAccessToken);
  } catch (error) {
    //Refresh token is bad, force log out
    trackError(error, "REFRESH_TOKEN_ERROR", {
      error: String(error),
      stack: error?.stack || new Error().stack,
    });
    SignolUtils.removeSessionCookies();
    if (window.location.pathname.split("/").length > 2)
      StorageUtils.setItem(
        LOGIN_PENDING_REDIRECT_COOKIE,
        window.location.pathname,
      );
    queryClient.clear();
    window.location.href = window.location.origin;
  }
  return request({ ...originalRequestOptions, isRetry: true });
};

async function request(options) {
  const {
    method = "GET",
    endpoint,
    body,
    headers: customHeaders,
    params: customParams,
    isRetry,
    ...customConfig
  } = options;
  const config = {
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers: {
      //Accept: 'application/json',
      "Content-Type": "application/json",
      ...customHeaders,
    },
    ...customConfig,
  };

  let withAuthTokens = false;
  const accessToken = StorageUtils.getItem(ACCESS_TOKEN_COOKIE);
  const refreshToken = StorageUtils.getItem(REFRESH_TOKEN_COOKIE);
  if (accessToken && refreshToken) {
    config.headers["Authorization"] = `Bearer ${accessToken}`;
    withAuthTokens = true;
  }

  const apiEndpoint = `${API_BASE_URL}${endpoint}`;
  const url = new URL(apiEndpoint);
  if (customParams) {
    Object.entries(customParams).forEach(([key, value]) => {
      if (value == null || (Array.isArray(value) && value.length === 0)) {
        //do nothing, omit empty
      } else if (Array.isArray(value) && String(value[0]).length) {
        //serialize array to eg `?teamIds[0]=101&teamIds[1]=102`
        value.forEach((item, idx) => {
          if (item !== undefined && item !== null) {
            url.searchParams.append(`${key}[${idx}]`, item);
          }
        });
      } else {
        url.searchParams.append(key, value);
      }
    });
  }

  let response;
  try {
    response = await fetch(url, config);
    if (!response.ok) {
      if (
        response.status === 401 &&
        !apiEndpoint.endsWith("/auth/refresh") &&
        refreshToken != null &&
        !isRetry
      ) {
        response = await refreshTokenAndRetry(refreshToken, {
          ...options,
          ...config,
        });
        return response;
      } else {
        let error = await response.text();
        error = JSON.parse(error || "{}");
        console.error({ error, response });
        return Promise.reject(error);
      }
    }
    const text = await response.text();
    try {
      return Promise.resolve(text ? JSON.parse(text) : {});
    } catch {
      console.warn(
        `apiClient: failed to parse json, returning response as string (${url})`,
      );
      return Promise.resolve(text);
    }
  } catch (err) {
    if (!window.navigator.onLine) {
      return Promise.resolve({ isOffline: true }); // this will force an error down the line and be caught by errorBoundary
    }
    // "JS Failed to fetch" --> TODO: fix APIGateway/Authorizer CORS Response Errors
    if (withAuthTokens) {
      console.warn("apiClient error, retrying...", err);
      response = await refreshTokenAndRetry(refreshToken, {
        ...options,
        ...config,
      });
      return response;
    }
    console.error("apiClient error", err);
    return Promise.reject(err);
  }
}

export { request };
